import React from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Trophy, Star } from 'lucide-react';
import StarRating from './StarRating';
import TierBadge from './TierBadge';

export default function AwardCard({ award, featured = false }) {
  const categoryIcons = {
    hotel: '🏨',
    resort: '🏝️',
    destination: '🌍'
  };
  // Prefer the auto-computed tier when ratings exist, fall back to manual tier
  const displayTier = award.auto_tier || award.tier;
  const isWinner = displayTier === 'gold';
  const hasFinalScore = typeof award.final_score === 'number';

  return (
    <Link
      to={award._href || `/awards/${award.id}`}
      className={`group award-card block relative overflow-hidden rounded-xl border bg-[#121212] ${
        isWinner ? 'border-[#D4AF37]/40' : 'border-white/5'
      } ${featured ? 'bento-large' : ''}`}
      data-testid={`award-card-${award.id}`}
    >
      {/* Image */}
      <div className={`relative ${featured ? 'h-80' : 'h-56'} overflow-hidden`}>
        <img
          src={award.image_url || 'https://images.unsplash.com/photo-1582533568805-78a15dcb01b5?w=800'}
          alt={award.name}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/40 to-transparent" />

        {/* Category Badge */}
        <div className="absolute top-4 left-4">
          <span className="px-3 py-1 rounded-full bg-black/50 backdrop-blur-sm text-white/90 text-sm capitalize flex items-center gap-2">
            <span>{categoryIcons[award.category] || '🏆'}</span>
            {award.category}
          </span>
        </div>

        {/* Winner Ribbon / Tier Badge */}
        <div className="absolute top-4 right-4">
          {isWinner ? (
            <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-[#D4AF37] text-[#0A0A0A] text-xs font-bold uppercase tracking-wider shadow-lg">
              <Trophy className="w-3.5 h-3.5" />
              Winner
            </span>
          ) : (
            <TierBadge tier={displayTier} size="sm" showLabel={false} />
          )}
        </div>
      </div>

      {/* Content */}
      <div className="p-5">
        {award.sub_award && (
          <p className="text-[10px] uppercase tracking-[0.2em] text-[#D4AF37]/80 mb-2 line-clamp-1">
            {award.sub_award}
          </p>
        )}

        <div className="flex items-start justify-between gap-4 mb-3">
          <h3 className="font-['Playfair_Display'] text-xl font-semibold text-white group-hover:text-[#D4AF37] transition-colors line-clamp-2">
            {award.name}
          </h3>
        </div>

        <div className="flex items-center gap-2 text-white/50 text-sm mb-3">
          <MapPin className="w-4 h-4" />
          <span>{award.location}, {award.country}</span>
        </div>

        <p className="text-white/60 text-sm line-clamp-2 mb-4">
          {award.description}
        </p>

        <div className="flex items-center justify-between pt-3 border-t border-white/5">
          <StarRating rating={award.star_rating} />
          {hasFinalScore ? (
            <div className="flex items-center gap-1.5 text-[#D4AF37] text-sm font-semibold" data-testid={`final-score-${award.id}`}>
              <Star className="w-4 h-4 fill-[#D4AF37]" />
              <span>{award.final_score.toFixed(1)}</span>
              <span className="text-white/40 font-normal">/ 100</span>
            </div>
          ) : (
            <span className="text-white/40 text-xs uppercase tracking-[0.2em]">
              Awaiting ratings
            </span>
          )}
        </div>
      </div>
    </Link>
  );
}
